/**
 * @module app.core
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { MemberService } from 'app/cd/services/member-service';
import { apiCall } from 'app/shared/lib/essette-api';
import { Toaster } from 'app/shared/lib/toaster';
import { IBaseConfig } from 'app/core/models/essette-interfaces';
import { forceNull } from 'app/shared/lib/utilities';
import { FlagOptions, IdentityOptions } from 'app/core/components/genders/genders';
import { I18N } from 'aurelia-i18n';
import * as Core from 'app/core/models/essette-api';

interface CustomSelectOption {
    display: string;
    enabled: boolean;
}

@autoinject()
export class SaveGender extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.twoWay, changeHandler: 'reset' })
    public gender: Core.Gender = {};
    protected genderId: string | null = null;

    @bindable
    protected config: IBaseConfig = {};

    protected genderText: string = '';
    protected genderCodeText: string = '';

    protected flags: Array<CustomSelectOption> = [];
    protected identity: CustomSelectOption | null = null;

    // Drop Down Options
    private flagOptions: Array<CustomSelectOption> = [];
    private identityOptions: Array<CustomSelectOption> = [];

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: MemberService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        if(!this.config) { throw new Error('Bindable property [config] was not set'); }

        this.service.configure(this.config);

        const initialFlagOptions = <string[]>Object.values(FlagOptions).slice(0, 2);
        initialFlagOptions.forEach(option => {
            this.flagOptions.push({
                display: this.i18n.tr(option),
                enabled: false
            });
        });

        const initialIdentityOptions = <string[]>Object.values(IdentityOptions).slice(0, 2);
        initialIdentityOptions.forEach(option => {
            this.identityOptions.push({
                display: this.i18n.tr(option),
                enabled: false
            });
        });

        this.reset();
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    protected buildGender(): Core.Gender {
        let availableAsSex: boolean = false, availableAsIdentity: boolean = false;

        this.flags.map(flag => flag.display).forEach(flag => {
            switch(flag) {
                case this.i18n.tr(FlagOptions.availableAsSex):
                    availableAsSex = true;
                    break;
                case this.i18n.tr(FlagOptions.availableAsIdentity):
                    availableAsIdentity = true;
                    break;
            }
        });

        const saveGender: Core.Gender = {
            id: this.gender.id,
            description: this.genderText,
            code: forceNull(this.genderCodeText),
            flag: {
                availableAsSex: availableAsSex,
                availableAsIdentity: availableAsIdentity
            },
            designation: {
                identity: this.identity?.display.substring(0, 1) || null
            }
        };

        return saveGender;
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        const isSuccess = await this.saveGender();
        return isSuccess;
    }

    public reset(): void {
        if(!this.gender) return;

        this.genderId = this.gender.id || null
        this.genderText = this.gender.description || '';
        this.genderCodeText = this.gender.code || '';
        this.flags = [];
        this.identity = null;

        const flags = this.gender.flag;
        if(flags) {
            // Set Flags
            this.flagOptions.forEach(option => {
                if(option.display === this.i18n.tr(FlagOptions.availableAsSex) && flags.availableAsSex) {
                    this.flags.push(option);
                }
                if(option.display === this.i18n.tr(FlagOptions.availableAsIdentity) && flags.availableAsIdentity) {
                    this.flags.push(option);
                }
            });
        }
        // Set Identity
        if(this.gender.designation?.identity === 'M') {
            this.identity = this.identityOptions[0];
        } else if(this.gender.designation?.identity === 'F') {
            this.identity = this.identityOptions[1];
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async saveGender(): Promise<boolean> {
        const insertGender: Core.Gender = this.buildGender();

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.saveGender(insertGender);
            isSuccessful = response.isSuccessful;

            return isSuccessful;
        }
        catch(error) {
            // if duplicate constraint violated on gender code, show toast
            if(error.status.errors) {
                const invalidId = error.status.errors.some((err: any) => err.code === 'property.constraintViolated' && err.property === 'code');
                if(invalidId) this.toaster.error().message(this.i18n.tr('ui:messages.warnings.inUse', { what: this.i18n.tr('cd:member.gender.code.$long') })).show();
            }
            throw error;
        }
        finally {
            if(this.genderId) {
                this.toaster.standard.updated('cd:member.gender.$', isSuccessful);
            } else {
                this.toaster.standard.created('cd:member.gender.$', isSuccessful);
            }
            this.state.endSave();
        }
    }
}
